import {
  canModerate,
  classifyQuality,
  effectiveMode,
  initialFallbackState,
  stepFallback,
  withOverride,
  type ChatMessage,
  type DeniedReason,
  type EventFrame,
  type FallbackState,
  type IceServer,
  type LogEntry,
  type MediaSource,
  type PeerInfo,
  type Quality,
  type Reaction,
  type RequestData,
  type ResponseData,
  type RoomSettings,
  type RoomState,
  type ServerEvents,
  type VideoMode,
  type WaitingInfo,
} from '@calling/shared';
import type { types as mc } from 'mediasoup-client';
import { api, ApiError, NetworkError } from '../lib/api';
import { acquire, cameraEncodings, playChime, watchLevel, type DeviceState } from '../lib/media';
import { meetingLimitsText } from '../lib/planText';
import { isNative, setScreenAwake, wsUrl } from '../lib/platform';
import { callPrefs, devicePrefs, guestSession, type DataMode } from '../lib/storage';
import { Store } from '../lib/useStore';
import { LARGE_SCREEN, preferredLayer } from './layout';
import { RpcError, Signaling, type SignalingStatus } from './signaling';
import { readStats, type CallStats, type StatTotals } from './stats';

export type Phase = 'joining' | 'waiting' | 'inCall' | 'finished';

/** Why the session is over; each maps to one screen in Meeting.tsx. */
export type FinishReason =
  | 'left'
  | 'ended'
  | 'timeLimit'
  | 'removed'
  | 'replaced'
  | 'lost'
  | DeniedReason
  | 'expired'
  // The meeting's owner can't use their account now (it was suspended): the link answers that the meeting isn't available.
  | 'unavailable'
  | 'notFound'
  | 'rateLimited'
  | 'error';

export interface Toast {
  id: number;
  text: string;
  tone: 'info' | 'warning' | 'danger';
  /** A button in the toast, such as Undo. */
  action?: { label: string; onClick: () => void };
}

export interface FloatingReaction {
  id: number;
  peerId: string;
  emoji: Reaction;
}

export interface RemoteMedia {
  audio?: MediaStreamTrack;
  camera?: MediaStreamTrack;
  screen?: MediaStreamTrack;
}

export interface LocalState {
  mic: DeviceState;
  cam: DeviceState;
  micOn: boolean;
  camOn: boolean;
  screenOn: boolean;
  cameraTrack: MediaStreamTrack | null;
  screenTrack: MediaStreamTrack | null;
  facing: 'user' | 'environment';
  /** Speaking while muted: shows the "You're muted" hint. */
  mutedSpeaking: boolean;
}

export interface CallState {
  phase: Phase;
  finish: { reason: FinishReason; message?: string; by?: string; owner?: boolean; minutes?: number | null } | null;
  hostPresent: boolean;
  connection: 'connected' | 'reconnecting' | 'lost';
  you: PeerInfo | null;
  room: RoomState | null;
  peers: PeerInfo[];
  waiting: WaitingInfo[];
  /** The join request currently shown as a popup. */
  popup: WaitingInfo | null;
  chat: ChatMessage[];
  unread: number;
  chatOpen: boolean;
  activeSpeaker: string | null;
  toasts: Toast[];
  reactions: FloatingReaction[];
  local: LocalState;
  media: Record<string, RemoteMedia>;
  videoMode: VideoMode;
  /** Why video is limited: the network, or the person's own data setting. */
  videoModeCause: 'network' | 'setting' | null;
  fallbackOverride: boolean;
  stats: CallStats | null;
  quality: Quality;
  /** `peerId` or `peerId:screen`, pinned by this viewer only. */
  pinned: string | null;
  dataMode: DataMode;
  speakerId: string;
  /** Set once the one-time "being recorded" notice was shown for this recording. */
  recordingNoticeFor: number | null;
  /** The server's clock minus this device's, in ms: times the server sends are on its clock. */
  clockOffset: number;
  /**
   * People this viewer chose "Hide for me" for, by peer ID: their video, sound,
   * messages, reactions and speaking ring are not shown or played here, for
   * the rest of the meeting. Nobody else is affected, and they aren't told.
   */
  hidden: string[];
}

export interface StartOptions {
  code: string;
  /** Guests only. */
  name?: string;
  audioTrack: MediaStreamTrack | null;
  videoTrack: MediaStreamTrack | null;
  mic: DeviceState;
  cam: DeviceState;
  micOn: boolean;
  camOn: boolean;
  /** Shown on the waiting screen before the server confirms the name. */
  displayName: string;
  /** The organiser's host link token, when the meeting was opened with one. */
  hostToken?: string;
}

interface ConsumerEntry {
  consumer: mc.Consumer;
  peerId: string;
  source: MediaSource;
  playing: boolean;
  layer: number | null;
}

const initialLocal = (o: StartOptions): LocalState => ({
  mic: o.mic,
  cam: o.cam,
  micOn: o.micOn && o.audioTrack !== null,
  camOn: o.camOn && o.videoTrack !== null,
  screenOn: false,
  cameraTrack: o.camOn ? o.videoTrack : null,
  screenTrack: null,
  facing: 'user',
  mutedSpeaking: false,
});

let nextLocalId = 1;

/**
 * One attempt to be in a meeting, from asking to join until leaving. It owns
 * the signaling connection, the WebRTC transports and all local tracks, and
 * exposes everything the screens need as one observable state object.
 */
export class CallSession extends Store<CallState> {
  private sig: Signaling | null = null;
  private device: mc.Device | null = null;
  private iceServers: IceServer[] = [];
  private sendTransport: mc.Transport | null = null;
  private recvTransport: mc.Transport | null = null;
  private readonly producers = new Map<MediaSource, mc.Producer>();
  private readonly consumers = new Map<string, ConsumerEntry>();
  private readonly visible = new Set<string>();
  private audioTrack: MediaStreamTrack | null;
  private mediaQueue: Promise<void> = Promise.resolve();
  private statsTimer: number | null = null;
  private totals: StatTotals | null = null;
  private fallback: FallbackState = initialFallbackState();
  private stopLevel: (() => void) | null = null;
  private loudSamples = 0;
  private recentLoud: boolean[] = [];
  private closed = false;
  private pageVisible = !document.hidden;
  private readonly recovering = new Set<string>();
  private readonly cleanups: Array<() => void> = [];

  constructor(private readonly options: StartOptions) {
    const prefs = callPrefs.get();
    super({
      phase: 'joining',
      finish: null,
      hostPresent: true,
      connection: 'connected',
      you: null,
      room: null,
      peers: [],
      waiting: [],
      popup: null,
      chat: [],
      unread: 0,
      chatOpen: false,
      activeSpeaker: null,
      toasts: [],
      reactions: [],
      local: initialLocal(options),
      media: {},
      videoMode: prefs.dataMode === 'audioOnly' ? 'audioOnly' : 'normal',
      videoModeCause: prefs.dataMode === 'audioOnly' ? 'setting' : null,
      fallbackOverride: false,
      stats: null,
      quality: 'good',
      pinned: null,
      dataMode: prefs.dataMode,
      speakerId: devicePrefs.get().speakerId ?? '',
      recordingNoticeFor: null,
      clockOffset: 0,
      hidden: [],
    });
    this.audioTrack = options.audioTrack;
    if (!options.camOn) options.videoTrack?.stop();
  }

  // ----------------------------------------------------------- lifecycle

  /** Asks to join. Resolves once the request is on its way (or was refused). */
  async start(): Promise<void> {
    let ticket: string;
    try {
      ticket = await this.requestTicket();
    } catch (err) {
      this.finishFromError(err);
      return;
    }
    if (this.closed) return;
    this.sig = new Signaling({
      url: wsUrl,
      initialTicket: ticket,
      getTicket: () => this.requestTicket(),
      onEvent: (frame) => this.onEvent(frame),
      onStatus: (status) => this.onStatus(status),
      onFatal: (err) => this.finishFromError(err),
    });
    this.sig.start();

    const online = () => this.sig?.retryNow();
    const visibility = () => {
      this.pageVisible = !document.hidden;
      this.syncConsumers();
      // In the native apps a backgrounded call keeps its audio but stops
      // sending video; others see the avatar (E8-S2).
      if (isNative) void this.setCameraPausedForBackground(document.hidden);
      if (!document.hidden) {
        this.sig?.retryNow();
        this.restoreMicrophone();
      }
    };
    // Turning a phone or tablet, or resizing a window, can make a screen
    // large or small enough to want other video sizes (see preferredLayer).
    const largeScreen = window.matchMedia(LARGE_SCREEN);
    const resized = () => this.syncConsumers();
    window.addEventListener('online', online);
    document.addEventListener('visibilitychange', visibility);
    largeScreen.addEventListener('change', resized);
    this.cleanups.push(() => {
      window.removeEventListener('online', online);
      document.removeEventListener('visibilitychange', visibility);
      largeScreen.removeEventListener('change', resized);
    });
  }

  private async requestTicket(): Promise<string> {
    const { code, name, hostToken } = this.options;
    const res = await api.request<{ ticket: string; guestToken?: string }>(`/api/rooms/${code}/join`, {
      method: 'POST',
      auth: 'optional',
      body: { name, hostToken, guestToken: hostToken ? undefined : guestSession.get(code) || undefined },
    });
    if (res.guestToken) guestSession.set(code, res.guestToken);
    return res.ticket;
  }

  private finishFromError(err: unknown): void {
    if (err instanceof ApiError) {
      const byCode: Record<string, FinishReason> = {
        blocked: 'blocked',
        locked: 'locked',
        full: 'full',
        cancelled: 'cancelled',
        ended: 'ended',
        expired: 'expired',
        unavailable: 'unavailable',
        room_not_found: 'notFound',
        rate_limited: 'rateLimited',
        host_link_invalid: 'error',
      };
      return this.finish(byCode[err.code] ?? 'error', { message: err.message });
    }
    this.finish('error', { message: err instanceof NetworkError ? err.message : 'Something went wrong while joining. Try again.' });
  }

  private finish(reason: FinishReason, extra: { message?: string; by?: string; owner?: boolean; minutes?: number | null } = {}): void {
    if (this.state.phase === 'finished') return;
    this.teardown();
    this.set({ phase: 'finished', finish: { reason, ...extra }, popup: null });
  }

  private teardown(): void {
    this.closed = true;
    for (const fn of this.cleanups.splice(0)) fn();
    if (this.statsTimer !== null) window.clearInterval(this.statsTimer);
    this.statsTimer = null;
    this.stopLevel?.();
    this.stopLevel = null;
    this.sig?.close();
    this.closeMedia();
    this.audioTrack?.stop();
    this.audioTrack = null;
    this.state.local.cameraTrack?.stop();
    this.state.local.screenTrack?.stop();
    this.options.videoTrack?.stop();
    void setScreenAwake(false);
    this.set({ local: { ...this.state.local, cameraTrack: null, screenTrack: null, micOn: false, camOn: false, screenOn: false }, media: {} });
  }

  private closeMedia(): void {
    this.sendTransport?.close();
    this.recvTransport?.close();
    this.sendTransport = null;
    this.recvTransport = null;
    this.producers.clear();
    this.consumers.clear();
  }

  /** Leaves the meeting and releases the camera and microphone (E4-S11). */
  async leave(): Promise<void> {
    if (this.state.phase === 'finished') return;
    await this.sig?.request('leave', {}, 2000).catch(() => {});
    this.finish('left');
  }

  /** Cancels a pending request to join and releases the devices. */
  async cancel(): Promise<void> {
    await this.sig?.request('leave', {}, 2000).catch(() => {});
    this.teardown();
  }

  get displayName(): string {
    return this.state.you?.name ?? this.options.displayName;
  }

  /** Best effort when the page is closing: tell the server now rather than after the heartbeat timeout. */
  leaveQuietly(): void {
    if (this.state.phase === 'finished') return;
    void this.sig?.request('leave', {}, 1000).catch(() => {});
  }

  /** Gives up the session without telling the user anything (the screen is unmounting). */
  dispose(): void {
    if (this.state.phase !== 'finished') {
      this.leaveQuietly();
      this.teardown();
    }
  }

  // -------------------------------------------------------------- events

  private onStatus(status: SignalingStatus): void {
    if (this.closed || this.state.phase === 'finished') return;
    if (status === 'open') this.set({ connection: 'connected' });
    else if (status === 'reconnecting') this.set({ connection: 'reconnecting' });
    else if (status === 'lost') {
      if (this.state.phase === 'inCall') this.finish('lost');
      else this.finish('error', { message: "We couldn't reach the server. Check your connection and try again." });
    } else if (status === 'closed') this.finish('replaced');
  }

  private toast(text: string, tone: Toast['tone'] = 'info', action?: Toast['action']): void {
    const toast = { id: nextLocalId++, text, tone, ...(action ? { action } : {}) };
    this.set({ toasts: [...this.state.toasts.slice(-3), toast] });
    window.setTimeout(() => this.dismissToast(toast.id), tone === 'info' && !action ? 4000 : 7000);
  }

  /** A short message on the call's screen, from the screen itself ("Report sent"). */
  notify(text: string, tone: Toast['tone'] = 'info'): void {
    this.toast(text, tone);
  }

  dismissToast(id: number): void {
    if (this.state.toasts.some((t) => t.id === id)) this.set({ toasts: this.state.toasts.filter((t) => t.id !== id) });
  }

  private patchPeer(peerId: string, patch: Partial<PeerInfo>): void {
    if (this.state.you?.id === peerId) {
      const you = { ...this.state.you, ...patch };
      this.set({ you });
      if (patch.role && !canModerate(you.role)) this.set({ waiting: [], popup: null });
      return;
    }
    this.set({ peers: this.state.peers.map((p) => (p.id === peerId ? { ...p, ...patch } : p)) });
  }

  private nameOf(peerId: string): string {
    return this.state.peers.find((p) => p.id === peerId)?.name ?? 'Someone';
  }

  private onEvent(frame: EventFrame): void {
    if (this.closed) return;
    const handlers: { [E in keyof ServerEvents]?: (data: ServerEvents[E]) => void } = {
      hello: (d) => this.set({ clockOffset: d.serverTime - Date.now() }),
      waiting: (d) => this.set({ phase: this.state.phase === 'inCall' ? 'inCall' : 'waiting', hostPresent: d.hostPresent }),
      admitted: (d) => this.onAdmitted(d),
      denied: (d) => this.finish(d.reason, { message: deniedMessage(d.reason) }),
      removed: (d) => this.finish('removed', { by: d.by }),
      ended: (d) =>
        d.reason === 'timeLimit'
          ? this.finish('timeLimit', { owner: this.state.you?.isOwner ?? false, minutes: this.state.room?.limits.maxMeetingMinutes ?? null })
          : this.finish('ended', { message: d.reason === 'hostEnded' ? 'The host ended the meeting' : 'The meeting has ended' }),
      replaced: () => this.finish('replaced'),
      peerJoined: (d) => this.set({ peers: [...this.state.peers.filter((p) => p.id !== d.peer.id), d.peer] }),
      peerLeft: (d) => {
        const media = { ...this.state.media };
        delete media[d.peerId];
        const pinned = this.state.pinned?.startsWith(d.peerId) ? null : this.state.pinned;
        this.set({ peers: this.state.peers.filter((p) => p.id !== d.peerId), media, pinned });
      },
      peerUpdated: (d) => {
        this.patchPeer(d.peerId, d.patch);
        if (d.patch.screenOn === false && this.state.pinned === `${d.peerId}:screen`) this.set({ pinned: null });
      },
      roomUpdated: (d) => this.onRoomUpdated(d.patch),
      waitingList: (d) => {
        const popup = this.state.popup && d.waiting.some((w) => w.id === this.state.popup!.id) ? this.state.popup : null;
        this.set({ waiting: d.waiting, popup });
      },
      joinRequest: (d) => {
        playChime();
        this.set({ popup: d.peer });
      },
      activeSpeaker: (d) => {
        this.set({ activeSpeaker: d.peerId });
        if (this.state.videoMode === 'speakerOnly') this.syncConsumers();
      },
      log: (d) => this.onLog(d.entry),
      newConsumer: (d) => this.enqueue(() => this.addConsumer(d)),
      consumerClosed: (d) => this.removeConsumer(d.consumerId),
      consumerPaused: () => this.publishMedia(),
      consumerResumed: () => this.publishMedia(),
      mutedByHost: (d) => {
        this.producers.get('mic')?.pause();
        this.set({ local: { ...this.state.local, micOn: false } });
        this.toast(`${d.by} muted you`, 'warning');
      },
      videoStoppedByHost: (d) => {
        this.dropLocalVideo('camera');
        this.dropLocalVideo('screen');
        this.toast(`${d.by} turned off your camera`, 'warning');
      },
      screenShareTakenOver: (d) => {
        this.dropLocalVideo('screen');
        this.toast(`${d.by} started presenting, so your screen share stopped`);
      },
      handLowered: (d) => {
        if (d.by) this.toast(`${d.by} lowered your hand`);
      },
      chat: (d) => {
        // Kept, so that "Show again" brings them back, but someone hidden makes no sound in the chat.
        if (this.isHidden(d.message.peerId)) return this.set({ chat: [...this.state.chat, d.message] });
        const closed = !this.state.chatOpen;
        this.set({ chat: [...this.state.chat, d.message], unread: closed ? this.state.unread + 1 : 0 });
        if (closed) this.toast(`${d.message.name}: ${d.message.text.length > 80 ? `${d.message.text.slice(0, 80)}…` : d.message.text}`);
      },
      chatRemoved: (d) => {
        const gone = new Set(d.messageIds);
        this.set({ chat: this.state.chat.filter((m) => !gone.has(m.id)) });
      },
      reaction: (d) => {
        if (this.isHidden(d.peerId)) return;
        const reaction = { id: nextLocalId++, peerId: d.peerId, emoji: d.emoji };
        this.set({ reactions: [...this.state.reactions, reaction] });
        window.setTimeout(() => this.set({ reactions: this.state.reactions.filter((r) => r.id !== reaction.id) }), 3000);
      },
    };
    (handlers[frame.event] as ((data: unknown) => void) | undefined)?.(frame.data);
  }

  private onLog(entry: LogEntry): void {
    switch (entry.type) {
      case 'joined':
        this.toast(`${entry.name} joined`);
        break;
      case 'left':
        this.toast(`${entry.name} left`);
        break;
      case 'removed':
        this.toast(`${entry.name} was removed`);
        break;
      case 'handRaised':
        this.toast(`${entry.name} raised a hand`);
        break;
      case 'recordingStopped':
        this.toast('Recording stopped');
        break;
      case 'recordingInterrupted':
        this.toast(
          entry.reason === 'limit'
            ? 'The recording reached the longest a recording can be, and was saved.'
            : entry.reason === 'storage'
              ? 'Recording stopped: the space for recordings is full. What was recorded so far has been kept.'
              : 'Recording interrupted. What was recorded so far has been kept.',
          'danger',
        );
        break;
      case 'recordingUnavailable':
        this.toast(entry.reason === 'storage_full' ? 'This meeting is not being recorded: recording storage is full.' : 'This meeting is not being recorded: recording could not be started.', 'danger');
        break;
      case 'endingSoon':
        this.toast(`This meeting ends in ${entry.minutes} minutes. It is at the time limit of the host’s plan.`, 'warning');
        break;
      case 'meetingFull':
        this.toast(`The meeting is full: its plan allows ${entry.max} people at a time. Everyone else is still waiting.`, 'warning');
        break;
      case 'locked':
        this.toast('The meeting is locked. Nobody new can ask to join.');
        break;
      case 'unlocked':
        this.toast('The meeting is unlocked.');
        break;
      case 'hostChanged':
        this.toast(entry.temporary ? `${entry.name} is host until the host returns` : `${entry.name} is now the host`);
        break;
      default:
        break;
    }
  }

  private onRoomUpdated(patch: Partial<RoomState>): void {
    const room = this.state.room ? { ...this.state.room, ...patch } : null;
    this.set({ room });
    if (patch.recording) this.announceRecording(patch.recording.startedAt);
    // The owner's plan got better while the meeting runs (they paid from another tab): say that it took effect.
    if (patch.limits && this.state.you?.isOwner) this.toast('Your new plan applies to this meeting now.');
    // HD came with it: everyone's camera is sent again at 720p.
    if (patch.limits?.hdVideo) this.refreshCameraQuality();
    if (patch.settings && this.state.you && !canModerate(this.state.you.role)) {
      if (!patch.settings.allowUnmute && this.state.local.micOn) void this.setMic(false);
    }
  }

  /** The one-time notice everyone sees when recording starts or when they join a recorded meeting (E7-S2). */
  private announceRecording(startedAt: number): void {
    if (this.state.recordingNoticeFor === startedAt) return;
    this.set({ recordingNoticeFor: startedAt });
    this.toast('This meeting is being recorded', 'danger');
  }

  private onAdmitted(d: ServerEvents['admitted']): void {
    const first = this.state.phase !== 'inCall';
    this.set({
      phase: 'inCall',
      connection: 'connected',
      you: d.you,
      room: d.room,
      peers: d.peers,
      waiting: d.waiting,
      popup: first ? (d.waiting[0] ?? null) : this.state.popup,
      chat: d.chat,
      unread: first ? 0 : this.state.unread,
      // Someone may have been talking since before this person came in (an older server does not say).
      activeSpeaker: d.activeSpeaker ?? null,
    });
    if (d.room.recording) this.announceRecording(d.room.recording.startedAt);
    if (first) {
      // The person whose plan it is hears about its limits before they matter.
      const limits = meetingLimitsText(d.room.limits);
      if (d.you.isOwner && limits) this.toast(`Your plan allows meetings of ${limits}.`, 'warning');
      void setScreenAwake(true);
      this.statsTimer = window.setInterval(() => void this.sampleStats(), 3000);
      this.watchMutedSpeech();
    }
    this.enqueue(() => this.setupMedia(d.roomToken));
    // Back after a dropped connection, on a weak one: the person speaking, whose video plays, may be someone else now.
    if (this.state.videoMode === 'speakerOnly') this.syncConsumers();
  }

  // --------------------------------------------------------------- media

  /** Media operations run one at a time so a reconnect cannot interleave with a toggle. */
  private enqueue(fn: () => Promise<void> | void): void {
    this.mediaQueue = this.mediaQueue.then(async () => {
      if (this.closed) return;
      try {
        await fn();
      } catch (err) {
        if (this.closed || (err instanceof RpcError && err.code === 'disconnected')) return;
        // The stream this referred to closed on the server while the request
        // was on its way; the event that says so arrives separately.
        if (err instanceof RpcError && err.code === 'not_found') return;
        console.warn('media operation failed', err);
      }
    });
  }

  private async setupMedia(roomToken: string): Promise<void> {
    const sig = this.sig!;
    const alive = (t: mc.Transport | null) => t !== null && !t.closed && t.connectionState !== 'failed';
    const fresh = !(alive(this.sendTransport) && alive(this.recvTransport));
    const res = await sig.request('media.join', { roomToken, fresh });
    this.iceServers = res.iceServers;
    if (!this.device) {
      // The WebRTC stack is loaded only when a call actually starts (E6-S9).
      const { Device } = await import('mediasoup-client');
      const device = new Device();
      await device.load({ routerRtpCapabilities: res.routerRtpCapabilities as mc.RtpCapabilities });
      this.device = device;
    }
    if (fresh || !res.resumed) {
      this.closeMedia();
      this.set({ media: {} });
      this.recvTransport = await this.createTransport('recv');
      this.sendTransport = await this.createTransport('send');
      await sig.request('media.setRtpCapabilities', { rtpCapabilities: this.device.rtpCapabilities });
      await this.startLocalMedia();
      return;
    }
    // The server kept our transports across the reconnect: refresh the
    // network path with an ICE restart instead of rebuilding the call.
    for (const transport of [this.sendTransport!, this.recvTransport!]) await this.restartIce(transport);
    const { consumerIds } = await sig.request('media.setRtpCapabilities', { rtpCapabilities: this.device.rtpCapabilities });
    for (const id of [...this.consumers.keys()]) if (!consumerIds.includes(id)) this.removeConsumer(id);
    const serverProducers = new Map(res.resumed.producers.map((p) => [p.id, p]));
    for (const [source, producer] of [...this.producers]) {
      const known = serverProducers.get(producer.id);
      if (!known) {
        // Stopped by a host while we were away.
        if (source === 'mic') {
          producer.close();
          this.producers.delete('mic');
        } else this.dropLocalVideo(source);
      } else if (known.paused && !producer.paused) {
        producer.pause();
      }
    }
    this.set({ local: { ...this.state.local, micOn: this.producers.has('mic') && !this.producers.get('mic')!.paused } });
  }

  private async createTransport(direction: 'send' | 'recv'): Promise<mc.Transport> {
    const sig = this.sig!;
    const params = await sig.request('media.createTransport', { direction });
    const options = {
      id: params.id,
      iceParameters: params.iceParameters as mc.IceParameters,
      iceCandidates: params.iceCandidates as mc.IceCandidate[],
      dtlsParameters: params.dtlsParameters as mc.DtlsParameters,
      iceServers: this.iceServers as RTCIceServer[],
    };
    const transport = direction === 'send' ? this.device!.createSendTransport(options) : this.device!.createRecvTransport(options);
    transport.on('connect', ({ dtlsParameters }, done, fail) => {
      sig.request('media.connectTransport', { transportId: transport.id, dtlsParameters }).then(() => done(), fail);
    });
    if (direction === 'send') {
      transport.on('produce', ({ kind, rtpParameters, appData }, done, fail) => {
        sig
          .request('media.produce', { transportId: transport.id, kind, rtpParameters, source: appData.source as MediaSource, paused: appData.paused === true })
          .then(({ producerId }) => done({ id: producerId }), fail);
      });
    }
    transport.on('connectionstatechange', (state) => {
      if (state === 'failed') this.enqueue(() => this.restartIce(transport));
      else if (state === 'disconnected') {
        // Often recovers by itself within a second or two; if not, restart ICE.
        window.setTimeout(() => {
          if (!transport.closed && transport.connectionState === 'disconnected') this.enqueue(() => this.restartIce(transport));
        }, 2500);
      }
    });
    return transport;
  }

  private async restartIce(transport: mc.Transport): Promise<void> {
    if (transport.closed || this.recovering.has(transport.id) || !this.sig?.connected) return;
    this.recovering.add(transport.id);
    try {
      const { iceParameters } = await this.sig.request('media.restartIce', { transportId: transport.id });
      await transport.restartIce({ iceParameters: iceParameters as mc.IceParameters });
    } finally {
      this.recovering.delete(transport.id);
    }
  }

  /** 720p is sent whenever the meeting's plan includes it, unless the person turned HD off. */
  private get hd(): boolean {
    return !callPrefs.get().hdOff && this.state.room?.limits.hdVideo !== false;
  }

  /** HD on or off (E6-S2). It applies at once to a camera that is on. */
  setHd(on: boolean): void {
    callPrefs.set({ hdOff: !on });
    this.refreshCameraQuality();
  }

  /**
   * Sends the camera again at the sizes the meeting now allows: after HD was
   * switched on or off, or when the owner's new plan brought HD to the
   * meeting. How many sizes a camera is sent in is fixed when it is first
   * sent, so it is sent afresh rather than adjusted. A camera that is paused
   * (audio only, or the app in the background) is left alone.
   */
  private refreshCameraQuality(): void {
    this.enqueue(async () => {
      const producer = this.producers.get('camera');
      const track = this.state.local.cameraTrack;
      if (!producer || producer.closed || producer.paused || !track || track.readyState !== 'live' || !this.sendTransport) return;
      const hd = this.hd;
      // The camera is asked for the size that goes with the setting; what it
      // delivers decides the sizes sent. (A camera another tab or app holds
      // at a smaller size stays at that size, and is sent without 720p.)
      await track.applyConstraints({ width: { ideal: hd ? 1280 : 640 }, height: { ideal: hd ? 720 : 360 }, frameRate: { ideal: 24, max: 30 } }).catch(() => {});
      if ((producer.rtpParameters.encodings?.length ?? 0) === cameraEncodings(track.getSettings(), hd).length) return;
      this.producers.delete('camera');
      producer.close();
      await this.sig!.request('media.closeProducer', { producerId: producer.id }).catch(() => {});
      await this.produceCamera(track);
    });
  }

  private mayUse(source: MediaSource): boolean {
    const { you, room } = this.state;
    if (!you || !room || canModerate(you.role)) return true;
    return source === 'mic' ? room.settings.allowUnmute : room.settings.allowVideo;
  }

  private async startLocalMedia(): Promise<void> {
    const { local } = this.state;
    if (this.audioTrack) {
      const on = local.micOn && this.mayUse('mic');
      await this.produceMic(this.audioTrack, !on);
      if (!on && local.micOn) this.set({ local: { ...this.state.local, micOn: false } });
    }
    if (local.camOn && local.cameraTrack) {
      if (this.mayUse('camera') && this.state.videoMode !== 'audioOnly') {
        // The preview ran at 320×240; step up to call resolution on the same track.
        await local.cameraTrack.applyConstraints({ width: { ideal: this.hd ? 1280 : 640 }, height: { ideal: this.hd ? 720 : 360 }, frameRate: { ideal: 24, max: 30 } }).catch(() => {});
        await this.produceCamera(local.cameraTrack);
      } else if (!this.mayUse('camera')) {
        this.dropLocalVideo('camera');
      }
    }
  }

  private async produceMic(track: MediaStreamTrack, paused: boolean): Promise<void> {
    const producer = await this.sendTransport!.produce({
      track,
      // Voice first: forward error correction and discontinuous transmission
      // keep speech clear and cheap on weak networks (E6-S1).
      codecOptions: { opusFec: true, opusDtx: true, opusMaxAverageBitrate: 32_000 },
      encodings: [{ maxBitrate: 32_000, priority: 'high', networkPriority: 'high' }],
      stopTracks: false,
      appData: { source: 'mic', paused },
    });
    if (paused) producer.pause();
    this.producers.set('mic', producer);
  }

  private async produceCamera(track: MediaStreamTrack): Promise<void> {
    // Simulcast: 180p and 360p always; 720p only when the meeting includes HD and the person wants it (E6-S2).
    const encodings = cameraEncodings(track.getSettings(), this.hd);
    const codec = this.device!.rtpCapabilities.codecs?.find((c) => c.mimeType.toLowerCase() === 'video/vp8');
    const producer = await this.sendTransport!.produce({
      track,
      encodings,
      codecOptions: { videoGoogleStartBitrate: 300 },
      codec,
      stopTracks: false,
      appData: { source: 'camera' },
    });
    this.producers.set('camera', producer);
    this.applySendLimits();
  }

  private dropLocalVideo(source: 'camera' | 'screen'): void {
    const producer = this.producers.get(source);
    producer?.close();
    this.producers.delete(source);
    const local = { ...this.state.local };
    if (source === 'camera') {
      // Stopping the track releases the camera, so its light goes off (E4-S4).
      local.cameraTrack?.stop();
      local.cameraTrack = null;
      local.camOn = false;
    } else {
      local.screenTrack?.stop();
      local.screenTrack = null;
      local.screenOn = false;
    }
    this.set({ local });
  }

  /** Mute or unmute (E4-S3). Returns false when it could not be done. */
  async setMic(on: boolean): Promise<boolean> {
    if (on && !this.mayUse('mic')) {
      this.toast('The host has turned off microphones for now.', 'warning');
      return false;
    }
    let replacement: { track: MediaStreamTrack; previous: MediaStreamTrack | null } | null = null;
    if (on && (!this.audioTrack || this.audioTrack.readyState === 'ended')) {
      const got = await acquire({ wantAudio: true, wantVideo: false, micId: devicePrefs.get().micId });
      this.set({ local: { ...this.state.local, mic: got.mic } });
      if (!got.audioTrack) return false;
      replacement = { track: got.audioTrack, previous: this.audioTrack };
      this.audioTrack = got.audioTrack;
      this.watchMutedSpeech();
    }
    return new Promise((resolve) => {
      this.enqueue(async () => {
        try {
          let producer = this.producers.get('mic');
          if (producer?.closed) {
            this.producers.delete('mic');
            producer = undefined;
          }
          if (producer && replacement) await producer.replaceTrack({ track: replacement.track });
          if (!producer && this.audioTrack && this.sendTransport) {
            await this.produceMic(this.audioTrack, !on);
            producer = this.producers.get('mic');
          }
          replacement?.previous?.stop();
          if (producer) {
            if (on) {
              await this.sig!.request('media.resumeProducer', { producerId: producer.id });
              producer.resume();
            } else {
              // Locally first: the microphone goes quiet at once (E4-S3).
              producer.pause();
              await this.sig!.request('media.pauseProducer', { producerId: producer.id });
            }
          }
          this.set({ local: { ...this.state.local, micOn: on, mutedSpeaking: false } });
          devicePrefs.set({ micOn: on });
          resolve(true);
        } catch (err) {
          if (err instanceof RpcError && err.code === 'disabled_by_host') this.toast('The host has turned off microphones for now.', 'warning');
          resolve(false);
        }
      });
    });
  }

  async setCamera(on: boolean): Promise<boolean> {
    if (!on) {
      this.enqueue(async () => {
        const producer = this.producers.get('camera');
        this.dropLocalVideo('camera');
        if (producer) await this.sig!.request('media.closeProducer', { producerId: producer.id }).catch(() => {});
      });
      devicePrefs.set({ camOn: false });
      return true;
    }
    if (!this.mayUse('camera')) {
      this.toast('The host has turned off video for now.', 'warning');
      return false;
    }
    if (this.state.videoMode === 'audioOnly') {
      this.toast(this.state.videoModeCause === 'setting' ? 'Audio-only mode is on. Change it in Settings to use your camera.' : 'Your connection is too weak for video right now.', 'warning');
      return false;
    }
    const prefs = devicePrefs.get();
    const got = await acquire({ wantAudio: false, wantVideo: true, camId: prefs.camId, hd: this.hd, facingMode: this.state.local.facing });
    this.set({ local: { ...this.state.local, cam: got.cam } });
    if (!got.videoTrack) return false;
    const track = got.videoTrack;
    return new Promise((resolve) => {
      this.enqueue(async () => {
        try {
          this.set({ local: { ...this.state.local, cameraTrack: track, camOn: true } });
          if (this.sendTransport) await this.produceCamera(track);
          devicePrefs.set({ camOn: true });
          resolve(true);
        } catch (err) {
          this.dropLocalVideo('camera');
          if (err instanceof RpcError && err.code === 'disabled_by_host') this.toast('The host has turned off video for now.', 'warning');
          resolve(false);
        }
      });
    });
  }

  /** Front/back camera on phones (E4-S9). */
  async flipCamera(): Promise<void> {
    const facing = this.state.local.facing === 'user' ? 'environment' : 'user';
    const got = await acquire({ wantAudio: false, wantVideo: true, facingMode: facing, hd: this.hd });
    if (!got.videoTrack) return;
    const track = got.videoTrack;
    this.enqueue(async () => {
      const old = this.state.local.cameraTrack;
      await this.producers.get('camera')?.replaceTrack({ track });
      old?.stop();
      this.set({ local: { ...this.state.local, cameraTrack: track, facing, camOn: true } });
    });
  }

  /** Switches microphone or camera mid-call without renegotiation (E4-S8). */
  async switchDevice(kind: 'mic' | 'cam', deviceId: string): Promise<void> {
    if (kind === 'mic') {
      devicePrefs.set({ micId: deviceId });
      const got = await acquire({ wantAudio: true, wantVideo: false, micId: deviceId });
      if (!got.audioTrack) return void this.toast('That microphone could not be used.', 'warning');
      const track = got.audioTrack;
      this.enqueue(async () => {
        const producer = this.producers.get('mic');
        await producer?.replaceTrack({ track });
        if (producer?.paused) track.enabled = false;
        this.audioTrack?.stop();
        this.audioTrack = track;
        this.watchMutedSpeech();
      });
      return;
    }
    devicePrefs.set({ camId: deviceId });
    if (!this.state.local.camOn) return;
    const got = await acquire({ wantAudio: false, wantVideo: true, camId: deviceId, hd: this.hd });
    if (!got.videoTrack) return void this.toast('That camera could not be used.', 'warning');
    const track = got.videoTrack;
    this.enqueue(async () => {
      const old = this.state.local.cameraTrack;
      await this.producers.get('camera')?.replaceTrack({ track });
      old?.stop();
      this.set({ local: { ...this.state.local, cameraTrack: track } });
    });
  }

  setSpeaker(deviceId: string): void {
    devicePrefs.set({ speakerId: deviceId });
    this.set({ speakerId: deviceId });
  }

  get someoneElsePresenting(): string | null {
    return this.state.peers.find((p) => p.screenOn)?.name ?? null;
  }

  /** Desktop screen sharing (E4-S10). */
  async startScreenShare(): Promise<void> {
    if (!this.mayUse('screen')) return void this.toast('The host has turned off video for now.', 'warning');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 5, max: 5 }, width: { max: 1920 }, height: { max: 1080 } }, audio: false });
    } catch (err) {
      // Closing the picker is not an error worth reporting.
      if ((err as DOMException).name !== 'NotAllowedError') this.toast('Your screen could not be shared.', 'warning');
      return;
    }
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    track.contentHint = 'detail';
    track.addEventListener('ended', () => void this.stopScreenShare());
    this.enqueue(async () => {
      try {
        const producer = await this.sendTransport!.produce({
          track,
          encodings: [{ maxBitrate: 1_500_000, maxFramerate: 5 }],
          stopTracks: false,
          appData: { source: 'screen' },
        });
        // Keep text sharp: drop frames before dropping resolution.
        const sender = producer.rtpSender;
        if (sender) {
          const p = sender.getParameters();
          p.degradationPreference = 'maintain-resolution';
          await sender.setParameters(p).catch(() => {});
        }
        this.producers.set('screen', producer);
        this.set({ local: { ...this.state.local, screenTrack: track, screenOn: true } });
      } catch {
        track.stop();
        this.toast('Your screen could not be shared.', 'warning');
      }
    });
  }

  async stopScreenShare(): Promise<void> {
    this.enqueue(async () => {
      const producer = this.producers.get('screen');
      this.dropLocalVideo('screen');
      if (producer) await this.sig!.request('media.closeProducer', { producerId: producer.id }).catch(() => {});
    });
  }

  private async setCameraPausedForBackground(hidden: boolean): Promise<void> {
    this.enqueue(async () => {
      const producer = this.producers.get('camera');
      if (!producer || this.state.videoMode === 'audioOnly') return;
      if (hidden && !producer.paused) {
        producer.pause();
        await this.sig!.request('media.pauseProducer', { producerId: producer.id });
      } else if (!hidden && producer.paused) {
        await this.sig!.request('media.resumeProducer', { producerId: producer.id });
        producer.resume();
      }
    });
  }

  /** Restores an intentionally-on microphone after a browser suspends its capture in a background tab. */
  private restoreMicrophone(): void {
    if (!this.state.local.micOn) return;
    this.enqueue(async () => {
      if (!this.state.local.micOn || !this.sendTransport || !this.sig?.connected) return;
      let track = this.audioTrack;
      if (!track || track.readyState === 'ended') {
        const got = await acquire({ wantAudio: true, wantVideo: false, micId: devicePrefs.get().micId });
        if (!got.audioTrack) {
          this.set({ local: { ...this.state.local, mic: got.mic, micOn: false } });
          devicePrefs.set({ micOn: false });
          this.toast('Your microphone stopped while this tab was in the background. Check permission and turn your microphone on again.', 'warning');
          return;
        }
        const previous = this.audioTrack;
        track = got.audioTrack;
        this.audioTrack = track;
        const producer = this.producers.get('mic');
        if (producer && !producer.closed) await producer.replaceTrack({ track });
        else await this.produceMic(track, false);
        previous?.stop();
        this.set({ local: { ...this.state.local, mic: got.mic } });
        this.watchMutedSpeech();
      }

      const producer = this.producers.get('mic');
      if (!producer || producer.closed) {
        await this.produceMic(track, false);
        return;
      }
      if (producer.paused) {
        await this.sig.request('media.resumeProducer', { producerId: producer.id });
        producer.resume();
      }
    });
  }

  // ----------------------------------------------------------- receiving

  private async addConsumer(d: ServerEvents['newConsumer']): Promise<void> {
    if (this.consumers.has(d.consumerId) || !this.recvTransport) return;
    const consumer = await this.recvTransport.consume({
      id: d.consumerId,
      producerId: d.producerId,
      kind: d.kind,
      rtpParameters: d.rtpParameters as mc.RtpParameters,
    });
    const entry: ConsumerEntry = { consumer, peerId: d.peerId, source: d.source, playing: false, layer: null };
    this.consumers.set(d.consumerId, entry);
    if (d.kind === 'audio' && !this.isHidden(d.peerId)) {
      // Audio always plays: it is what keeps a call alive on a bad network. Not someone this viewer hid.
      await this.sig!.request('media.resumeConsumer', { consumerId: d.consumerId });
      entry.playing = true;
    }
    this.publishMedia();
    this.syncConsumers();
  }

  private removeConsumer(consumerId: string): void {
    const entry = this.consumers.get(consumerId);
    if (!entry) return;
    entry.consumer.close();
    this.consumers.delete(consumerId);
    this.publishMedia();
  }

  /** Rebuilds the peer → tracks map the tiles render from. */
  private publishMedia(): void {
    const media: Record<string, RemoteMedia> = {};
    for (const { consumer, peerId, source, playing } of this.consumers.values()) {
      if (this.isHidden(peerId)) continue;
      const slot = (media[peerId] ??= {});
      if (source === 'mic') slot.audio = consumer.track;
      else if (playing) slot[source] = consumer.track;
    }
    this.set({ media });
  }

  /** Tiles report when they scroll in or out of view (E6-S5). */
  setTileVisible(key: string, visible: boolean): void {
    if (visible === this.visible.has(key)) return;
    if (visible) this.visible.add(key);
    else this.visible.delete(key);
    this.syncConsumers();
  }

  private wantsVideo(entry: ConsumerEntry): boolean {
    if (!this.pageVisible || this.isHidden(entry.peerId)) return false;
    const mode = this.state.videoMode;
    if (mode === 'audioOnly') return false;
    const key = entry.source === 'screen' ? `${entry.peerId}:screen` : entry.peerId;
    if (!this.visible.has(key)) return false;
    // On a weak connection only the person speaking (or presenting) keeps video.
    if (mode === 'speakerOnly') return entry.source === 'screen' || entry.peerId === this.state.activeSpeaker;
    return true;
  }

  /** Pauses and resumes video consumers on the SFU to match what is on screen. */
  private syncConsumers(): void {
    this.enqueue(async () => {
      const sig = this.sig;
      if (!sig?.connected) return;
      const tiles = this.visible.size;
      const large = window.matchMedia(LARGE_SCREEN).matches;
      let changed = false;
      for (const [id, entry] of this.consumers) {
        if (entry.consumer.kind !== 'video') continue;
        const want = this.wantsVideo(entry);
        if (want !== entry.playing) {
          await sig.request(want ? 'media.resumeConsumer' : 'media.pauseConsumer', { consumerId: id });
          entry.playing = want;
          changed = true;
        }
        if (!want) continue;
        const key = entry.source === 'screen' ? `${entry.peerId}:screen` : entry.peerId;
        const main = entry.source === 'screen' || this.state.pinned === key;
        const layer = this.state.videoMode === 'speakerOnly' ? 0 : preferredLayer(tiles, main, this.state.dataMode === 'dataSaver', large);
        if (layer !== entry.layer) {
          await sig.request('media.setPreferredLayers', { consumerId: id, spatialLayer: layer });
          entry.layer = layer;
        }
      }
      if (changed) this.publishMedia();
    });
  }

  pin(key: string | null): void {
    this.set({ pinned: key });
    this.syncConsumers();
  }

  // ------------------------------------------------- network adaptation

  private async sampleStats(): Promise<void> {
    if (this.closed || !this.sendTransport || !this.recvTransport) return;
    let send: RTCStatsReport | null = null;
    let recv: RTCStatsReport | null = null;
    try {
      [send, recv] = await Promise.all([this.sendTransport.getStats(), this.recvTransport.getStats()]);
    } catch {
      return;
    }
    const { stats, totals } = readStats(send as never, recv as never, this.totals, performance.now());
    this.totals = totals;
    const quality = classifyQuality(stats.sample);
    if (quality !== this.state.quality) void this.sig?.request('peer.setQuality', { quality }).catch(() => {});
    this.set({ stats, quality });

    // A struggling device stops sending the 720p layer (E6-S2).
    const camera = this.producers.get('camera');
    if (camera && stats.cpuLimited && (camera.maxSpatialLayer ?? 2) > 1) await camera.setMaxSpatialLayer(1).catch(() => {});

    if (this.state.dataMode === 'audioOnly') return;
    this.fallback = stepFallback(this.fallback, stats.sample);
    this.applyVideoMode(effectiveMode(this.fallback), 'network');
  }

  private applyVideoMode(mode: VideoMode, cause: 'network' | 'setting'): void {
    const before = this.state.videoMode;
    this.set({ videoMode: mode, videoModeCause: mode === 'normal' ? null : cause, fallbackOverride: this.fallback.override });
    if (mode === before) return;
    this.syncConsumers();
    this.enqueue(async () => {
      const camera = this.producers.get('camera');
      if (!camera) return;
      if (mode === 'audioOnly' && !camera.paused) {
        camera.pause();
        await this.sig!.request('media.pauseProducer', { producerId: camera.id });
      } else if (mode !== 'audioOnly' && camera.paused && !document.hidden) {
        await this.sig!.request('media.resumeProducer', { producerId: camera.id });
        camera.resume();
      }
      this.applySendLimits();
    });
  }

  private applySendLimits(): void {
    const camera = this.producers.get('camera');
    if (!camera) return;
    const low = this.state.videoMode === 'speakerOnly' || this.state.dataMode === 'dataSaver';
    void camera.setMaxSpatialLayer(low ? 0 : 2).catch(() => {});
  }

  /** "Try video anyway": keep video on despite the weak connection (E6-S3). */
  tryVideoAnyway(): void {
    this.fallback = withOverride(this.fallback, true);
    this.applyVideoMode(effectiveMode(this.fallback), 'network');
  }

  setDataMode(dataMode: DataMode): void {
    callPrefs.set({ dataMode });
    this.set({ dataMode });
    if (dataMode === 'audioOnly') this.applyVideoMode('audioOnly', 'setting');
    else this.applyVideoMode(effectiveMode(this.fallback), 'network');
    this.applySendLimits();
    this.syncConsumers();
  }

  private watchMutedSpeech(): void {
    this.stopLevel?.();
    this.stopLevel = null;
    if (!this.audioTrack) return;
    this.stopLevel = watchLevel(this.audioTrack, (_level, raw) => {
      // Detected on the device only; nothing is sent while muted.
      if (this.state.local.micOn || this.state.phase !== 'inCall') {
        this.recentLoud = [];
        return;
      }
      // Speech comes in bursts, so count loud moments over the last two
      // seconds; a single click or bump is not enough to show the hint.
      this.recentLoud.push(raw > 0.2);
      if (this.recentLoud.length > 33) this.recentLoud.shift();
      this.loudSamples = this.recentLoud.filter(Boolean).length;
      if (this.loudSamples >= 4 && !this.state.local.mutedSpeaking) {
        this.recentLoud = [];
        this.set({ local: { ...this.state.local, mutedSpeaking: true } });
        window.setTimeout(() => this.set({ local: { ...this.state.local, mutedSpeaking: false } }), 4000);
      }
    });
  }

  // ------------------------------------------- chat, hands, moderation

  setChatOpen(open: boolean): void {
    this.set({ chatOpen: open, unread: open ? 0 : this.state.unread });
  }

  async sendChat(text: string): Promise<boolean> {
    try {
      const { message } = await this.sig!.request('chat.send', { text });
      this.set({ chat: [...this.state.chat, message] });
      return true;
    } catch (err) {
      this.toast(err instanceof RpcError ? err.message : 'Your message was not sent.', 'warning');
      return false;
    }
  }

  async setHand(raised: boolean): Promise<void> {
    await this.sig?.request('hand.set', { raised }).catch(() => {});
  }

  async react(emoji: Reaction): Promise<void> {
    await this.sig?.request('reaction.send', { emoji }).catch(() => {});
  }

  dismissPopup(): void {
    this.set({ popup: null });
  }

  // ------------------------------------------- hiding and reporting

  isHidden(peerId: string): boolean {
    return this.state.hidden.includes(peerId);
  }

  /**
   * "Hide for me": this viewer stops seeing and hearing someone, and their
   * messages and reactions, for the rest of the meeting. Their sound stops on
   * the server too, so none of it reaches this device. They aren't told.
   */
  hide(peerId: string, name = this.nameOf(peerId)): void {
    if (this.isHidden(peerId) || peerId === this.state.you?.id) return;
    this.set({ hidden: [...this.state.hidden, peerId], pinned: this.state.pinned?.startsWith(peerId) ? null : this.state.pinned });
    this.setPeerSound(peerId, false);
    this.syncConsumers();
    this.publishMedia();
    this.toast(`${name.split(' ')[0]} is hidden for you. They aren’t told.`, 'info', { label: 'Undo', onClick: () => this.show(peerId) });
  }

  /** "Show again": undoes hide(). */
  show(peerId: string): void {
    if (!this.isHidden(peerId)) return;
    this.set({ hidden: this.state.hidden.filter((id) => id !== peerId) });
    this.setPeerSound(peerId, true);
    this.syncConsumers();
    this.publishMedia();
  }

  /** Pauses or resumes, on the server, the sound this device receives from one person. */
  private setPeerSound(peerId: string, on: boolean): void {
    this.enqueue(async () => {
      for (const [id, entry] of this.consumers) {
        if (entry.peerId !== peerId || entry.consumer.kind !== 'audio' || entry.playing === on || !this.sig?.connected) continue;
        await this.sig.request(on ? 'media.resumeConsumer' : 'media.pauseConsumer', { consumerId: id });
        entry.playing = on;
      }
    });
  }

  /**
   * Reports someone in the meeting to the people who run the service. Nobody
   * in the meeting is told. Throws the server's refusal (an RpcError), or
   * `disconnected` / `timeout` when it could not be sent.
   */
  report(input: RequestData<'report.send'>): Promise<ResponseData<'report.send'>> {
    if (!this.sig) return Promise.reject(new RpcError('disconnected', 'Not connected.'));
    return this.sig.request('report.send', input, 15_000);
  }

  /** Runs a host action and reports a refusal as a toast. */
  private async act(run: () => Promise<unknown>, failure: string): Promise<boolean> {
    try {
      await run();
      return true;
    } catch (err) {
      this.toast(err instanceof RpcError && err.code !== 'timeout' && err.code !== 'disconnected' ? err.message : failure, 'warning');
      return false;
    }
  }

  admit = (peerId: string) => this.act(() => this.sig!.request('host.admit', { peerId }), 'That person could not be admitted.');
  deny = (peerId: string) => this.act(() => this.sig!.request('host.deny', { peerId }), 'That request could not be denied.');
  admitAll = () => this.act(() => this.sig!.request('host.admitAll', {}), 'Could not admit everyone.');
  mute = (peerId: string) => this.act(() => this.sig!.request('host.mute', { peerId }), 'Could not mute.');
  muteAll = () => this.act(() => this.sig!.request('host.muteAll', {}), 'Could not mute everyone.');
  stopVideo = (peerId: string) => this.act(() => this.sig!.request('host.stopVideo', { peerId }), "Could not stop that person's video.");
  remove = (peerId: string) => this.act(() => this.sig!.request('host.remove', { peerId }), 'Could not remove that person.');
  lowerHand = (peerId: string) => this.act(() => this.sig!.request('host.lowerHand', { peerId }), 'Could not lower the hand.');
  /** Host switches update at once and are put back if the server refuses. */
  private async optimistic(patch: Partial<RoomState>, run: () => Promise<unknown>, failure: string): Promise<boolean> {
    const before = this.state.room;
    if (before) this.set({ room: { ...before, ...patch } });
    const ok = await this.act(run, failure);
    if (!ok && before && this.state.room) this.set({ room: { ...this.state.room, locked: before.locked, settings: before.settings } });
    return ok;
  }

  setLocked = (locked: boolean) => this.optimistic({ locked }, () => this.sig!.request('host.setLocked', { locked }), 'Could not change the lock.');
  setSettings = (patch: Partial<RoomSettings>) =>
    this.optimistic({ settings: { ...this.state.room!.settings, ...patch } }, () => this.sig!.request('host.setSettings', patch), 'Could not change the setting.');
  setCohost = (peerId: string, cohost: boolean) => this.act(() => this.sig!.request('host.setCohost', { peerId, cohost }), 'Could not change the role.');
  transferHost = (peerId: string) => this.act(() => this.sig!.request('host.transferHost', { peerId }), 'Could not change the host.');
  startRecording = () => this.act(() => this.sig!.request('recording.start', {}, 20_000), 'Recording could not be started.');
  stopRecording = () => this.act(() => this.sig!.request('recording.stop', {}, 20_000), 'Recording could not be stopped.');

  async endForAll(): Promise<void> {
    await this.act(() => this.sig!.request('host.end', {}), 'The meeting could not be ended.');
  }
}

function deniedMessage(reason: DeniedReason): string {
  switch (reason) {
    case 'denied':
    case 'blocked':
      return "You can't join this meeting";
    case 'locked':
      return 'This meeting is locked';
    case 'full':
      return 'Too many people are waiting to join. Try again in a few minutes.';
    case 'timeout':
      return 'No one responded';
    case 'cancelled':
      return 'Cancelled by host';
    case 'ended':
      return 'This meeting has ended';
  }
}
